import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cn } from '@relay-towing/tokens';
import {
  controlRowRecipe,
  radioGroupRecipe,
  radioIndicatorRecipe,
  radioRecipe,
  type RadioGroupRecipeProps,
  type RadioRecipeProps,
} from '../recipes/control.recipe';
import { fieldDescriptionRecipe } from '../recipes/field.recipe';
import { Label } from './Field';
import { useFieldControlProps } from './field-context';

type ControlSize = NonNullable<RadioRecipeProps['size']>;

/**
 * The size chosen on the group, so every option in it matches without each one
 * having to be told. Radix gives us no way to read the Root's own props from an
 * Item, so this is a plain context.
 */
const RadioSizeContext = React.createContext<ControlSize>('sm');

export interface RadioGroupProps
  extends Omit<
      React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>,
      'orientation'
    >,
    RadioGroupRecipeProps {
  size?: ControlSize;
}

/**
 * A pick-exactly-one control.
 *
 * Use it over a Select when there are two to five options and seeing them all
 * at once helps — a date range, a payment method. Past that a Select is kinder,
 * because a wall of radios is hard to scan.
 *
 *   <RadioGroup value={range} onValueChange={setRange}>
 *     <Radio value="7d" label="Last 7 days" />
 *     <Radio value="30d" label="Last 30 days" />
 *   </RadioGroup>
 *
 * Arrow keys move between options and selection follows focus, which is the
 * standard radio behaviour Radix gives us — so a keyboard user does not have to
 * press space on each one.
 */
export const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(function RadioGroup(
  { className, orientation, size = 'sm', id, disabled, required, ...props },
  ref
) {
  const field = useFieldControlProps({ id, disabled, required });

  return (
    <RadioSizeContext.Provider value={size}>
      <RadioGroupPrimitive.Root
        ref={ref}
        id={field.id}
        disabled={field.disabled}
        required={field.required}
        aria-describedby={field['aria-describedby']}
        aria-invalid={field['aria-invalid']}
        className={cn(radioGroupRecipe({ orientation }), className)}
        {...props}
      />
    </RadioSizeContext.Provider>
  );
});

export interface RadioProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>,
    'children'
  > {
  /** Text beside the dot. Clicking it selects, because Label wires htmlFor. */
  label?: React.ReactNode;
  /** Helper line under the label. */
  description?: React.ReactNode;
  size?: ControlSize;
}

/**
 * One option within a RadioGroup.
 *
 * Named `Radio` rather than `RadioGroupItem` because that is what it is called
 * everywhere except in Radix's own source.
 */
export const Radio = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioProps
>(function Radio({ className, label, description, size, id, ...props }, ref) {
  const generated = React.useId();
  const groupSize = React.useContext(RadioSizeContext);
  const resolved = size ?? groupSize;
  const controlId = id ?? `${generated}-radio`;
  const descriptionId = description ? `${generated}-description` : undefined;

  const dot = (
    <RadioGroupPrimitive.Item
      ref={ref}
      id={controlId}
      aria-describedby={descriptionId}
      className={cn(radioRecipe({ size: resolved }), className)}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        className={radioIndicatorRecipe({ size: resolved })}
      />
    </RadioGroupPrimitive.Item>
  );

  if (!label && !description) return dot;

  return (
    <div className={controlRowRecipe()}>
      {dot}
      <div className="relay-ui-internal grid gap-0.5 leading-none">
        {label ? <Label htmlFor={controlId}>{label}</Label> : null}
        {description ? (
          <p id={descriptionId} className={fieldDescriptionRecipe()}>
            {description}
          </p>
        ) : null}
      </div>
    </div>
  );
});
